import type { QuestionnaireTheme } from '@shared/types';
import { z } from 'zod';

export const QUESTIONNAIRE_EXPORT_FORMAT = 'curium-questionnaire';
export const QUESTIONNAIRE_EXPORT_VERSION = 1;

const questionnaireThemeSchema = z.object({
    fontSize: z.number().optional(),
    fontFamily: z.string().optional(),
    colorPrimary: z.string().optional(),
    colorError: z.string().optional(),
    backButtonLabel: z.string().optional(),
    backButtonUrl: z.string().optional(),
    colorLink: z.string().optional(),
    colorLinkHover: z.string().optional(),
    colorLinkActive: z.string().optional(),
}) satisfies z.ZodType<QuestionnaireTheme>;

/**
 * Shallow card shape only — deep structural validation (root card, nextCardId
 * references, child invariants) is `cleanAndValidateQuestionnaire`'s job at
 * import time. Legacy definitions may carry numeric ids; the cleaner coerces
 * them to strings.
 */
const exportedCardSchema = z.looseObject({
    id: z.union([z.string(), z.number()]),
    type: z.string(),
    title: z.string().optional(),
});

/**
 * Every transferable field of a questionnaire — everything except `id`,
 * `orgId`, and `isDeleted`. `notificationSendTo` stores lowercase
 * QUESTIONNAIRE_NOTIFICATION_RECIPIENTS strings ('broker', 'policy_holder',
 * 'driver', ...), not the uppercase Prisma enum, so it stays a lenient string
 * array; the importer filters it against that list.
 */
const exportedQuestionnaireSchema = z.object({
    name: z.string().min(1),
    definition: z.array(exportedCardSchema).nullable().optional(),
    declarationTitle: z.string().nullable().optional(),
    declarationBody: z.string().nullable().optional(),
    confirmationBody: z.string().nullable().optional(),
    notificationSendTo: z.array(z.string()).nullable().optional(),
    subject: z.string().nullable().optional(),
    emailBody: z.string().nullable().optional(),
    smsBody: z.string().nullable().optional(),
    logoUrl: z.string().nullable().optional(),
    faviconUrl: z.string().nullable().optional(),
    theme: questionnaireThemeSchema.nullable().optional(),
});

export const questionnaireExportEnvelopeSchema = z.object({
    format: z.literal(QUESTIONNAIRE_EXPORT_FORMAT),
    version: z.literal(QUESTIONNAIRE_EXPORT_VERSION),
    exportedAt: z.string(),
    /** bigint serialised as string. */
    sourceOrgId: z.string(),
    questionnaire: exportedQuestionnaireSchema,
});

export type ExportedQuestionnaire = z.infer<typeof exportedQuestionnaireSchema>;
export type QuestionnaireExportEnvelope = z.infer<typeof questionnaireExportEnvelopeSchema>;
